import { AddressDtoSchema } from "@modules/address/addressDto"
import { PhoneNumberWithPrefixSchema } from "@modules/box/boxTypes"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import {
  DisputeReason,
  DisputeStatus,
  PaymentMethodType,
  TransactionStatus,
} from "@modules/finances/types/financesType"
import { z } from "zod"

export const TransactionCouponDetailsDtoSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  type: z.nativeEnum(CouponType),
})
export type TransactionCouponDetailsDto = z.infer<typeof TransactionCouponDetailsDtoSchema>

export const TransactionPromoCodeDetailsDtoSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  type: z.nativeEnum(CouponType),
  name: z.string().nullable(),
})
export type TransactionPromoCodeDetailsDto = z.infer<typeof TransactionPromoCodeDetailsDtoSchema>

export const TransactionUserDtoSchema = z.object({
  id: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  username: z.string(),
  avatar: HUImageDtoSchema.nullable(),
})

export const TransactionUserDetailsDtoSchema = z.object({
  id: z.string(),
  boxMemberNumber: z.string().nullable(),
  firstname: z.string(),
  lastname: z.string(),
  username: z.string(),
  avatar: HUImageDtoSchema.nullable(),
  address: AddressDtoSchema.nullable(),
  email: z.string(),
  nationality: z.string().nullable(),
  phoneNumber: PhoneNumberWithPrefixSchema.nullable(),
  stripeCustomerId: z.string(),
})

export enum TransactionProductType {
  PROGRAM_VIRTUAL = "PROGRAM_VIRTUAL",
  PROGRAM_ON_OFF = "PROGRAM_ON_OFF",
  PROGRAM_BUNDLE_VIRTUAL = "PROGRAM_BUNDLE_VIRTUAL",
  PROGRAM_BUNDLE_ON_OFF = "PROGRAM_BUNDLE_ON_OFF",
  BOX_OFFER_SUBSCRIPTION = "BOX_OFFER_SUBSCRIPTION",
  BOX_OFFER_TRIAL = "BOX_OFFER_TRIAL",
  BOX_OFFER_TICKETS_BOOK = "BOX_OFFER_TICKETS_BOOK",
  BOX_OFFER_DROP_IN = "BOX_OFFER_DROP_IN",
  BOX_OFFER_MILLENIUM = "BOX_OFFER_MILLENIUM",
}

export const TransactionProductDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(TransactionProductType),
})

export const TransactionDtoSchema = z.object({
  transactionId: z.string(),
  date: z.string().datetime(),
  user: TransactionUserDtoSchema,
  amount: z.number(),
  fee: z.number(),
  netAmount: z.number(), // netAmount = Amout - fee
  currency: z.string(),
  status: z.nativeEnum(TransactionStatus),
  disputed: z.boolean(),
  refunded: z.boolean(),
  product: TransactionProductDtoSchema,
  paymentMethodType: z.nativeEnum(PaymentMethodType),
})

export enum DiscountType {
  DISCOUNT = "DISCOUNT",
  PROMO_CODE = "PROMO_CODE",
}

export const TransactionLineDtoSchema = z.object({
  description: z.string().nullable(),
  netAmount: z.number(),
  taxAmount: z.number().nullable(),
  totalAmount: z.number(), // totalAmount = netAmount - taxAmount
  taxPercent: z.number().nullable(),
  currency: z.string(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
})

export const TransactionDiscountDtoSchema = z.object({
  amount: z.number(),
  currency: z.string().nullable(), // Null uniquement si CouponType == PERCENT
  couponType: z.nativeEnum(CouponType),
  name: z.string().nullable(),
  type: z.nativeEnum(DiscountType),
})

export const TransactionDetailsDtoSchema = z.object({
  transactionId: z.string(),
  refundable: z.boolean(),
  date: z.string().datetime(),
  user: TransactionUserDetailsDtoSchema,
  amount: z.number(),
  netAmount: z.number(), // netAmount = Amout - fee
  totalTaxAmount: z.number(),
  fee: z.number(),
  currency: z.string(),
  status: z.nativeEnum(TransactionStatus),
  disputed: z.boolean(),
  refunded: z.boolean(),
  invoiceNumber: z.string(),
  invoicePdfUrl: z.string().nullable(),
  lines: z.array(TransactionLineDtoSchema),
  discounts: z.array(TransactionDiscountDtoSchema),
  paymentMethodDetail: z.object({
    type: z.nativeEnum(PaymentMethodType),
    last4Digits: z.string().nullable(),
  }),
  disputeDetail: z
    .object({
      amount: z.number(),
      fee: z.number(),
      status: z.nativeEnum(DisputeStatus),
      reason: z.nativeEnum(DisputeReason),
    })
    .nullable(),
})

export type TransactionDto = z.infer<typeof TransactionDtoSchema>
export type TransactionDetailsDto = z.infer<typeof TransactionDetailsDtoSchema>
export type TransactionLineDto = z.infer<typeof TransactionLineDtoSchema>
export type TransactionDiscountDto = z.infer<typeof TransactionDiscountDtoSchema>
export type TransactionProductDto = z.infer<typeof TransactionProductDtoSchema>
export type TransactionUserDto = z.infer<typeof TransactionUserDtoSchema>
export type TransactionUserDetailsDto = z.infer<typeof TransactionUserDetailsDtoSchema>
